import { isDateInPast } from "@/helpers/date";
import { isAppLoadedInsideIframe } from "@/helpers/smart-storage";
import mailboxNudge from "./mail/mailbox-nudge.store";

const getDefaultState = () => ({
  plans: [],
});

const nestedModules = {
  mailboxNudge,
};

export default {
  namespaced: true,
  modules: nestedModules,
  state: () => getDefaultState(),
  actions: {
    plans({ commit }, payload) {
      commit("plans", payload);
    },
    resetState({ commit, dispatch }) {
      // Cascade into nested modules, same pattern as the root store.
      Object.keys(nestedModules).forEach(key => {
        dispatch(`${key}/resetState`);
      });
      commit("resetState");
    },
  },
  mutations: {
    plans(state, payload) {
      state.plans = payload;
    },
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
  },
  getters: {
    plans: state => state.plans,
    planId: (state, getters, rootState) => rootState.currentPlanDetails?.mailSuiteDetails?.planId,
    currentProductDetails: (state, getters, rootState) =>
      rootState.currentPlanDetails?.mailSuiteDetails || {},
    currentPlanType: (state, getters) => getters.currentProductDetails?.planDetails?.type,
    isPaidPlan: (state, getters) => getters.currentProductDetails?.planDetails?.isPaid,
    isTrialPlan: (state, getters) => getters.currentProductDetails?.planDetails?.isTrial,
    isFreePlan: (state, getters) => !getters.isPaidPlan && !getters.isTrialPlan,
    canUpgradePlan: (state, getters) => {
      return state.plans.some(plan => plan.id !== getters.planId);
    },
    isExpired: (state, getters) =>
      getters.currentProductDetails?.expiry
        ? isDateInPast(getters.currentProductDetails.expiry)
        : false,
    orderId: (state, getters) => getters.currentProductDetails?.orderId,
    /**
     * Number of mailbox licenses still available to spend on new mailboxes.
     * Derived from `mailSuiteDetails.noOfAccounts` (same shape consumed
     * by `currentProductDetails` above) — top-level `currentPlanDetails`
     * does NOT carry `noOfAccounts` directly, so going through this
     * getter reuses the correct path. Falls back gracefully when fields
     * are missing so this is safe to read at any lifecycle.
     */
    availableLicenses: (state, getters, rootState, rootGetters) => {
      const counts = getters.currentProductDetails?.noOfAccounts || {};
      const limit = counts.limit ?? counts.purchased ?? 0;
      const active = counts.active ?? (rootGetters.currentDomainMailboxDetails || []).length;
      return Math.max(0, limit - active);
    },
    /** True once at least one mailbox exists on the order. */
    hasMailboxes: (state, getters, rootState, rootGetters) =>
      (rootGetters.currentDomainMailboxDetails || []).length > 0,
    /**
     * Raw partner-flag rule behind the widget's "Buy email accounts" tile:
     * hidden when the partner suppresses the section or the plan isn't paid.
     * Consumed verbatim by cpWidget so the tile's semantics are unchanged
     * (the widget home is a plain route and can render outside an iframe).
     * `flags` is legitimately undefined before partner info is bootstrapped.
     */
    isBuyMoreAccountsAllowed: (state, getters, rootState, rootGetters) =>
      !(rootGetters.flags?.hideIFrameBuyMoreAccountSection || !getters.isPaidPlan),
    /**
     * Whether an out-of-license "Buy" path should be offered.
     *
     * Gated on:
     *  - `isPaidPlan` — a trial/free plan can't buy more accounts, so the CTA
     *    falls back to Create (the used-license screen) rather than a dead Buy
     *    path. (Reads false before the plan is fetched, which fails safe.)
     *  - `paymentEnabled` — the partner-level billing switch; if the partner has
     *    payments disabled, Buy is a dead path everywhere.
     *  - iframe/widget additionally honours `hideIFrameBuyMoreAccountSection`.
     */
    canBuyMoreAccounts: (state, getters, rootState, rootGetters) =>
      getters.isPaidPlan &&
      rootGetters.paymentEnabled &&
      (!isAppLoadedInsideIframe() || getters.isBuyMoreAccountsAllowed),
  },
};
